/**
 * @fileoverview Active Clients page.
 * @description Banks whose initial payment is confirmed, read from the Sales CRM clients APIs
 * (Confluence 2.8.25): the four global KPI tiles, a grid filtered, sorted and paged by the backend
 * (search, State, Owner, Active or Archived), and one client's details with Invite HR Admin,
 * Resend, Edit, Archive and Restore, each offered only when the details list it in `allowedActions`.
 * Every Sales role reads every client; the backend masks a client's contacts for a Manager or
 * Executive who does not own it. A grid read that fails keeps the filters on screen, with the error
 * in place of the rows, so the person can change or clear what they asked for; rows from the
 * previous filters stay dimmed while the new ones load. The page only lays the sections out; the
 * state lives in useActiveClientsScreen and the dialogs in ActiveClientsDialogs.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ActiveClientsDialogs,
  ClientsFiltersBar,
  ClientsPager,
  ClientsTable,
  PageLoadError,
  TableSkeleton,
  useActiveClientsScreen,
} from '@/features/sales'
import KpiStrip from '@/shared/components/molecules/KpiStrip'
import KpiStripSkeleton from '@/shared/components/molecules/KpiStripSkeleton'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { useFlashMessage } from '@/shared/hooks/useFlashMessage'

import type { ClientDialogTarget, ClientSummary } from '@/features/sales'

/**
 * @description The four KPI tiles; global, the same for every role and every filter.
 * @param {{ summary: ClientSummary }} props - The summary.
 * @param {ClientSummary} props.summary - The backend's four counts.
 */
function ClientKpis({ summary }: { summary: ClientSummary }) {
  const { t } = useTranslation()

  return (
    <KpiStrip
      tiles={[
        {
          label: t('sales.activeClients.kpi.total'),
          sublabel: t('sales.activeClients.kpi.totalSublabel'),
          icon: 'briefcase',
          iconTone: 'navy',
          value: summary.totalClients,
          testId: 'active-clients-kpi-total',
        },
        {
          label: t('sales.activeClients.kpi.branches'),
          sublabel: t('sales.activeClients.kpi.branchesSublabel'),
          icon: 'grid',
          iconTone: 'blue',
          value: summary.branches,
          testId: 'active-clients-kpi-branches',
        },
        {
          label: t('sales.activeClients.kpi.hrInvited'),
          sublabel: t('sales.activeClients.kpi.hrInvitedSublabel'),
          icon: 'hourglass',
          iconTone: 'amber',
          value: summary.hrAdminInvited,
          testId: 'active-clients-kpi-hr-invited',
          tone: summary.hrAdminInvited > 0 ? 'warning' : undefined,
        },
        {
          label: t('sales.activeClients.kpi.hrActive'),
          sublabel: t('sales.activeClients.kpi.hrActiveSublabel'),
          icon: 'check',
          iconTone: 'green',
          value: summary.hrAdminActive,
          testId: 'active-clients-kpi-hr-active',
        },
      ]}
    />
  )
}

/**
 * @component
 */
function ActiveClientsPage() {
  const { t } = useTranslation()
  const screen = useActiveClientsScreen()
  const flash = useFlashMessage()
  const [target, setTarget] = useState<ClientDialogTarget | null>(null)
  const { clientsQuery, summaryQuery, optionsQuery } = screen
  const page = clientsQuery.data

  const header = (
    <PageHeader
      title={t('sales.activeClients.title')}
      description={t('sales.activeClients.description')}
    />
  )

  if (clientsQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <KpiStripSkeleton />
        <TableSkeleton columns={10} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {header}

      {summaryQuery.isSuccess ? (
        <ClientKpis summary={summaryQuery.data} />
      ) : summaryQuery.isError ? (
        <PageLoadError
          message={t('sales.activeClients.kpiLoadError')}
          testId="active-clients-kpi-error"
        />
      ) : (
        <KpiStripSkeleton />
      )}

      {flash.message && (
        <div
          className="flex items-center gap-2.5 rounded-lg border border-success/30 bg-success-bg px-3.5 py-2.5"
          data-testid="active-clients-flash"
        >
          <span role="status" className="flex-1 text-compact font-medium text-success">
            {flash.message}
          </span>
          <button
            type="button"
            onClick={flash.dismiss}
            data-testid="active-clients-flash-dismiss-button"
            className="shrink-0 cursor-pointer text-xs font-semibold text-success underline"
          >
            {t('sales.pipeline.flash.dismiss')}
          </button>
        </div>
      )}

      <section className="rounded-card border border-divider bg-white">
        <ClientsFiltersBar
          values={screen.filterState}
          options={optionsQuery.data}
          onChange={screen.changeFilters}
          onClear={screen.clearFilters}
          anyFilter={screen.anyFilter}
          total={page?.meta.total}
        />
        {page ? (
          <div
            className={`transition-opacity ${clientsQuery.isPlaceholderData ? 'opacity-50' : ''}`}
            aria-busy={clientsQuery.isPlaceholderData}
            data-testid="active-clients-grid"
          >
            <ClientsTable
              clients={page.items}
              sort={screen.sort}
              anyFilter={screen.anyFilter}
              onSort={screen.changeSort}
              onView={(client) =>
                setTarget({ tenantId: client.tenantId, bankName: client.bankName, view: 'details' })
              }
              onClear={screen.clearFilters}
            />
            <ClientsPager meta={page.meta} shown={page.items.length} onPage={screen.setPage} />
          </div>
        ) : (
          <div className="p-4">
            <PageLoadError
              message={t('sales.activeClients.loadError')}
              testId="active-clients-load-error"
            />
          </div>
        )}
      </section>

      <ActiveClientsDialogs target={target} onTarget={setTarget} onFlash={flash.show} />
    </div>
  )
}

export default ActiveClientsPage
